import React, {useEffect, useState} from 'react';
import styles from "./getHistory.module.scss";
import {Button, DatePicker, DatePickerProps, message as messageAnt, Select, SelectProps} from "antd";
import {useHttp} from "../../../hooks/http.hook";
import {TimeFrames} from "../../../shared/types";
import {v4 as uuidv4} from 'uuid';
import {CountryExchange} from "../../../shared/countryExchange";

interface Props {
  country: CountryExchange
  tickerList: string[]
  forwardTickerListWithHistory: (tickerList: string[]) => void
}

const GetHistory = ({country, tickerList, forwardTickerListWithHistory}: Props) => {

  const {loading, request, error, clearError} = useHttp()

  const [tickerForGetHistory, setTickerForGetHistory] = useState<string>()//тикер выбранный в селекте для получения исторических данных
  const [timeframeForGetHistory, setTimeframeForGetHistory] = useState<string>()//таймфрейм выбранный в селекте для получения исторических данных
  const [optionsSelectTicker, setOptionsSelectTicker] = useState<SelectProps['options']>()//массив тикеров, доступных для выбора
  const [optionsSelectTimeframe, setOptionsSelectTimeframe] = useState<SelectProps['options']>()//массив таймфреймов, доступных для выбора
  const [dateFrom, setDateFrom] = useState<string>()//начало периода, для которого нужно получить данные
  const [dateTill, setDateTill] = useState<string>()//конец периода, для которого нужно получить данные
  const [shortInfo, setShortInfo] = useState<Record<TimeFrames, string> | undefined>()//краткая сводка об наличии истории на сервере по выбранному тикеру
  const [tickerListWithHistory, setTickerListWithHistory] = useState<string[] | undefined>()//список тикеров с уже загруженной историей
  const [btnDisabled, setBtnDisabled] = useState(true)//блокировка кнопки если не все необходимые данные выбраны

  //функция загрузки списка тикеров с уже загруженной историей для наглядности
  const getTickerListWithHistory = async (country: CountryExchange) => {
    try {
      const data = await request('/api/history/ticker-list', 'POST', {
        country: country
      })
      messageAnt.info(data.message)
      return JSON.parse(data.info)
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Что-то пошло не так'
      messageAnt.info(message);
      clearError()
    }
  }

  //изначальная загрузка списка тикеров с уже загруженной историей для наглядности
  useEffect(() => {
    const get = async (country: CountryExchange) => {
      const info = await getTickerListWithHistory(country)
      setTickerListWithHistory(info)
    }
    get(country)
  }, [country])

  //поднятие списка тикеров с уже полученной историей на верхний компонент Testing для того чтобы в компоненте StrategiesWrap в селекторе выбора тикера для тестов был только этот список а не полный
  useEffect(() => {
    if (tickerListWithHistory) {
      forwardTickerListWithHistory(tickerListWithHistory)
    }
  }, [tickerListWithHistory])

  //формирование списков для селекторов выбора
  useEffect(() => {
    const optionsTicker: SelectProps['options'] = []
    tickerList.forEach((ticker) => {
      optionsTicker.push({value: ticker, label: ticker})
    })
    setOptionsSelectTicker(optionsTicker)
    const optionsTimeframe: SelectProps['options'] = []
    Object.keys(TimeFrames).forEach((timeFrame) => {
      optionsTimeframe.push({value: timeFrame, label: timeFrame})
    })
    setOptionsSelectTimeframe(optionsTimeframe)
  }, [tickerList, TimeFrames, country])

  //подгрузка краткой сводки об уже существующей исторической информации по выбранному тикеру
  const getShortInfo = async (country: string, ticker: string) => {
    try {
      const data = await request('/api/history/short-info', 'POST', {
        country: country,
        ticker: ticker,
      })
      messageAnt.info(data.message)
      return JSON.parse(data.info)
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Что-то пошло не так'
      messageAnt.info(message);
      clearError()
    }
  }
  const getShortInfoLocalFunc = async (country: string, ticker: string | undefined) => {
    if (ticker) {
      const info = await getShortInfo(country, ticker)
      setShortInfo(info)
    } else {
      setShortInfo(undefined)
    }
  }
  useEffect(() => {
    getShortInfoLocalFunc(country, tickerForGetHistory)
  }, [tickerForGetHistory])

  //выбор инструмента для получения исторических данных
  const onChangeTicker = (value: string) => {
    setTickerForGetHistory(value)
  };
  //выбор таймфрейма для получения исторических данных
  const onChangeTimeframe = (value: string) => {
    setTimeframeForGetHistory(value)
  };
  //выбор начала периода, для которого нужно получить данные
  const dateFromChangeHandler: DatePickerProps['onChange'] = (date, dateString) => {
    setDateFrom(dateString)
  };
  //выбор конца периода, для которого нужно получить данные
  const dateTillChangeHandler: DatePickerProps['onChange'] = (date, dateString) => {
    setDateTill(dateString)
  };

  //запрос на получение сервером исторических данных
  const getHistoryHandler = async (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    try {
      const data = await request('/api/history', 'POST', {
        country: country,
        ticker: tickerForGetHistory,
        dateFrom: dateFrom,
        till: dateTill,
        interval: timeframeForGetHistory
      })
      messageAnt.info(data.message)
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Что-то пошло не так'
      messageAnt.info(message);
      clearError()
    }
    await getShortInfoLocalFunc(country, tickerForGetHistory)
  }

  //управление активностью кнопки запуска
  useEffect(() => {
    if (dateFrom && dateTill && tickerForGetHistory && timeframeForGetHistory) {
      setBtnDisabled(false)
    } else {
      setBtnDisabled(true)
    }
  }, [dateFrom, dateTill, tickerForGetHistory, timeframeForGetHistory])

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.wrap}>
          <h5>Исторические данные</h5>
          <div className={[styles.tickerList, styles.blockWrap].join(' ')}>
            <p className={styles.title}>История уже получена для:</p>
            <div className={styles.titleWrap}>
              <p className={styles.line}>
                {
                  tickerListWithHistory ?
                    tickerListWithHistory.map(ticker => (
                      <span key={uuidv4()}>{ticker} </span>
                    )) :
                    null
                }
              </p>
            </div>
          </div>
          <div className={[styles.settings, styles.blockWrap].join(' ')}>
            <p className={styles.title}>Настройки получения данных</p>
            <Select showSearch allowClear placeholder="Тикер" onChange={onChangeTicker}
                    options={optionsSelectTicker} style={{width: 152}} size={'small'}/>
            <DatePicker placeholder="Начальная дата" onChange={dateFromChangeHandler}
                        style={{width: 152, height: 24}} size={'small'} popupStyle={{top: '140px', left: '23px'}}/>
            <DatePicker placeholder="Конечная дата" onChange={dateTillChangeHandler}
                        style={{width: 152, height: 24}} size={'small'} popupStyle={{top: '140px', left: '23px'}}/>
            <Select showSearch allowClear placeholder="Таймфрейм" onChange={onChangeTimeframe}
                    options={optionsSelectTimeframe} style={{width: 152}} size={'small'}/>
            <Button title={'Загрузить историю на сервер'} type={'primary'} onClick={getHistoryHandler}
                    loading={loading} style={{width: 152}} size={'small'} disabled={btnDisabled}>Получить</Button>
          </div>
          {
            tickerForGetHistory ?
              <div className={[styles.received, styles.blockWrap].join(' ')}>
                <p className={styles.title}>Уже загруженные на сервер
                  данные{tickerForGetHistory ? ' по ' + tickerForGetHistory + ':' : ':'}</p>
                <div>
                  {
                    shortInfo ?
                      Object.keys(shortInfo).map(tf => (
                        <p className={styles.line} key={uuidv4()}>
                          <span className={styles.bold}>{tf}:</span> {shortInfo[tf as keyof typeof shortInfo]}</p>
                      )) :
                      null
                  }
                </div>
                <p className={styles.tip}>Для тестов лучше получить все ТФ на одинаковый период</p>
              </div>
              :
              null
          }
        </div>
      </div>
    </div>
  );
};

export default GetHistory;